import * as React from "react";
import { observer } from "mobx-react";
import {
    Body1,
    FluentProvider,
    TeachingPopover,
    TeachingPopoverBody,
    TeachingPopoverFooter,
    TeachingPopoverSurface,
    TeachingPopoverTitle
} from "@microsoft/shifts-ui-core";
import { ITagEntity, UserFRETypes } from "sh-models";
import { InstrumentationService } from "sh-services";
import { setFREsShownSoFar } from "sh-stores";
import StringsStore from "sh-strings/store";
import ThemeUtils from "sh-application/utility/ThemeUtils";

import { useCoachmarkStyles } from "./MuteScheduleGroupCoachmark.styles";

/**
 * LOCAL TESTING FLAG
 * Set to true to force-show the coachmark for local testing (bypasses FRE check).
 * This allows testing the coachmark without clearing IndexedDB or modifying user settings.
 *
 * IMPORTANT: Keep this set to FALSE when committing to ensure normal production behavior.
 */
const FORCE_SHOW_COACHMARK_FOR_LOCAL_TESTING = false;

interface MuteScheduleGroupCoachmarkProps {
    /** Ref to the more menu button that the coachmark should target */
    targetRef: React.RefObject<HTMLElement>;
    /** Whether this is the first schedule group */
    isFirstGroup: boolean;
    /** The tag/schedule group entity (used for debug logging only) */
    tag: ITagEntity;
    /** Array of FREs that have been shown so far (for reactivity to changes) */
    fresShownSoFar?: UserFRETypes[];
}

/**
 * Coachmark (teaching bubble) for mute schedule group / unmute schedule group feature.
 *
 * Teaches users they can selectively mute schedule groups to control shift request notifications.
 * Shown once on first schedule group, first page load (FRE), points to "..." more menu.
 * Tracked in UserSettings.fresShownSoFar to avoid showing again.
 *
 * Uses @observer decorator to reactively respond to tour system state changes.
 */
export const MuteScheduleGroupCoachmark: React.FC<MuteScheduleGroupCoachmarkProps> = observer(({ targetRef, isFirstGroup, tag, fresShownSoFar = [] }) => {
    const styles = useCoachmarkStyles();
    const [hasLocallyDismissedCoachmark, setHasLocallyDismissedCoachmark] = React.useState(false);

    // Delayed visibility flag to prevent flickering between tour transitions.
    const [shouldShowDelayed, setShouldShowDelayed] = React.useState(false);

    // Track when the target element becomes available.
    // The parent populates targetRef.current via a DOM query in its own useEffect,
    // but ref mutations don't trigger re-renders. We use requestAnimationFrame to
    // check after all parent effects have completed (RAF fires after effects), then
    // set state to trigger a single re-render so conditionsMet can be re-evaluated.
    const [isTargetMounted, setIsTargetMounted] = React.useState(!!targetRef.current);
    React.useEffect(() => {
        if (targetRef.current) {
            if (!isTargetMounted) { setIsTargetMounted(true); }
            return;
        }
        const rafId = requestAnimationFrame(() => {
            if (targetRef.current) {
                setIsTargetMounted(true);
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, []);

    // Get localized strings
    const strings = StringsStore().registeredStringModules.get("schedulePage").strings;
    const commonStrings = StringsStore().registeredStringModules.get("common").strings;

    // Check FREs from prop for reactivity (component re-renders when fresShownSoFar changes)
    const smartSchedulingIntroSeen = fresShownSoFar.includes(UserFRETypes.SmartSchedulingIntro);
    const muteScheduleGroupsIntroSeen = fresShownSoFar.includes(UserFRETypes.MuteScheduleGroupsIntro);

    // Check all conditions required to show the coachmark (excluding the delay)
    const conditionsMet =
        // Only show on first schedule group to avoid repetition
        isFirstGroup &&
        // User hasn't seen this coachmark before (or force flag is enabled for local testing)
        (FORCE_SHOW_COACHMARK_FOR_LOCAL_TESTING || !muteScheduleGroupsIntroSeen) &&
        // User hasn't dismissed the coachmark in this session
        !hasLocallyDismissedCoachmark &&
        // Target element (more menu button) is mounted and available
        isTargetMounted &&
        // Show only after the Smart Scheduling coachmark has been shown
        smartSchedulingIntroSeen;

    // Delay showing the coachmark to prevent flickering.
    React.useEffect(() => {
        if (conditionsMet) {
            const timer = setTimeout(() => {
                setShouldShowDelayed(true);
            }, 2500);
            return () => clearTimeout(timer);
        } else {
            // Immediately hide if conditions no longer met
            setShouldShowDelayed(false);
        }
    }, [conditionsMet]);

    // Final visibility decision: all conditions met AND delay period completed
    const shouldShowCoachmark = conditionsMet && shouldShowDelayed;

    /**
     * Handler: persist FRE as seen and log dismissal
     */
    const handleDismiss = React.useCallback(async () => {
        setHasLocallyDismissedCoachmark(true);
        try {
            await setFREsShownSoFar([UserFRETypes.MuteScheduleGroupsIntro]);
        } finally {
            InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupCoachMarkDismissed, []);
        }
    }, []);

    // Handle TeachingPopover open state change (dismiss on close)
    const handlePopoverOpenChange = React.useCallback(
        (_: unknown, data: { open: boolean }) => {
            if (!data.open) {
                handleDismiss();
            }
        },
        [handleDismiss]
    );

    // Log when coachmark is shown
    React.useEffect(() => {
        if (shouldShowCoachmark) {
            const timer = setTimeout(() => {
                InstrumentationService.logEvent(InstrumentationService.events.MuteScheduleGroupCoachMarkShown, []);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [shouldShowCoachmark]);

    return (
        <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
            <TeachingPopover
                open={shouldShowCoachmark}
                onOpenChange={handlePopoverOpenChange}
                positioning={{
                    target: targetRef.current,
                    position: "below",
                    align: "center",
                    offset: { mainAxis: 2 }
                }}
            >
                <TeachingPopoverSurface className={styles.teachingPopoverSurface}>
                    <TeachingPopoverBody>
                        <TeachingPopoverTitle>
                            {strings.get("muteScheduleGroupCoachmarkTitle")}
                        </TeachingPopoverTitle>
                        <Body1 className={styles.popoverBodyText}>
                            {strings.get("muteScheduleGroupCoachmarkContent")}
                        </Body1>
                    </TeachingPopoverBody>
                    <TeachingPopoverFooter
                        primary={{ children: commonStrings.get("gotIt"), onClick: handleDismiss }}
                    />
                </TeachingPopoverSurface>
            </TeachingPopover>
        </FluentProvider>
    );
});

export default MuteScheduleGroupCoachmark;
